import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { Flex, TextButton, ToggleButton, Text } from "@amzn/storm-ui";
import { useEffect, useRef, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import { useStore } from "zustand";

import { ACCOUNT_AGNOSTIC_VIEW_WITH_ACCOUNT_SWITCHER } from "../../../config/constants";
import { IHubContentTypeToLabelMap } from "../../../constants/filters";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../constants/telemetry";
import { useFilterTypesLoader } from "../../../hooks/filterTypes/useFilterTypesLoader";
import useEncodedFilterStateUrl from "../../../hooks/misc/useEncodedFilterStateUrl";
import { FeatureFlagsStore } from "../../../store/featureFlags";
import { discoveryFilterStore } from "../../../store/filters";
import { navigationStore } from "../../../store/navigation";
import { DEFAULT_ALL_ITEM, type Item } from "../../../types/common";
import type {
    DiscoveryFilter,
    DiscoveryFilterField,
} from "../../../types/filters/common";
import { IHubContentType } from "../../../types/ihubContentType";
import { IHubContentProvider } from "../../../types/providers";
import { copyToClipboard } from "../../../utils/clipboard.utils";
import { getAdvertiserId, QueryParam } from "../../../utils/permission.utils";
import {
    logWithTakt,
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
} from "../../../utils/telemetry.utils";

import AdditionalFilters from "./AdditionalFilters";
import FilterButton from "./FilterButton";
import SearchBar from "./SearchBar";
import TypeFilterButton from "./TypeFilterButton";
import { FilterContainer, StyledPill } from "./discoveryFilterSharedStyles";

export const DiscoveryFilters = (): ReactElement => {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [searchParams, setSearchParam] = useSearchParams();
    const [setEncodedFilterStateUrl, getEncodedFilterStateUrl] =
        useEncodedFilterStateUrl();
    const [insightIdentifier, setIdentifier] = useStore(
        navigationStore,
        (state) => [state.identifier, state.setIdentifier],
    );

    const toggleElRef = useRef(null);

    const [
        contentType,
        contentSubType,
        filters,
        hasAppliedFilters,
        setField,
        clearFieldSelections,
        resetFilters,
    ] = useStore(discoveryFilterStore, (state) => [
        state.contentType,
        state.getContentSubType(),
        state.getFilters(),
        state.hasAppliedFilters,
        state.setField,
        state.clearFieldSelections,
        state.resetFilters,
    ]);

    const {
        visibleFilters,
        additionalFilters,
        fieldSelections,
        filterOptions,
    } =
        filters[
            useUnifiedSearch
                ? IHubContentProvider.UNIFIED
                : contentSubType.provider
        ];

    const enableSingleIdentifierView = insightIdentifier;

    const advertiserId = getAdvertiserId();

    const handleRemoveAllFilters = (): void => {
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();

        logWithTakt(TELEMETRY_ID.CLEAR_ADDED_FILTERS_CLICK, {
            ...filters,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.CLEAR_ADDED_FILTERS_CLICK,
            PHOENIX_FEATURE.FILTERS,
            {
                customData: {
                    filters,
                },
            },
        );
        resetFilters();
        setDefaultTypeFilter(contentType, true);
        setEncodedFilterStateUrl();
    };

    const handleOptionClick = (field: DiscoveryFilterField, option: Item) => {
        if (option.label === DEFAULT_ALL_ITEM.label) {
            clearFieldSelections(field);
        } else {
            setField(field, option, false);
        }
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();

        logWithTakt(TELEMETRY_ID.FILTER_OPTION_SELECTION, {
            field,
            option,
            ...filters,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.FILTER_OPTION_SELECTION,
            PHOENIX_FEATURE.FILTERS,
            {
                customData: {
                    field,
                    option,
                    filters,
                },
            },
        );
        setEncodedFilterStateUrl();
    };

    const [filterTypesLoaderStatus, typeFilterGroups, setDefaultTypeFilter] =
        useFilterTypesLoader();

    useEffect(() => {
        if (!searchParams.get(QueryParam.FILTERS)) {
            setDefaultTypeFilter();
        }
    }, [filterTypesLoaderStatus, setDefaultTypeFilter]);

    const clearSingleIdentifierView = () => {
        setIdentifier(undefined);
    };

    const handleContentToggle = (contentType: IHubContentType) => {
        if (enableSingleIdentifierView) {
            handlePillClose();
        }

        setDefaultTypeFilter(contentType);
        logWithTakt(TELEMETRY_ID.ALL_INSIGHTS_CONTENT_TYPE_CHANGE, {
            contentType,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.ALL_INSIGHTS_CONTENT_TYPE_CHANGE,
            PHOENIX_FEATURE.FILTERS,
            {
                customData: {
                    contentType,
                },
            },
        );
        if (!enableSingleIdentifierView) {
            setEncodedFilterStateUrl();
        }
    };

    const handleShareFilterClick = () => {
        copyToClipboard(getEncodedFilterStateUrl(), "copied-url");
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();
        logWithTakt(TELEMETRY_ID.SHARE_FILTER_CLICK, {
            ...filters,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.SHARE_FILTER_CLICK,
            PHOENIX_FEATURE.FILTERS,
            {
                customData: {
                    filters,
                },
            },
        );
    };

    const handlePillClose = () => {
        if (enableSingleIdentifierView) {
            clearSingleIdentifierView();
            setDefaultTypeFilter(contentType);
            discoveryFilterStore.getState().setSearchQuery("");
        }
    };

    return (
        <FilterContainer data-testid="discovery-filter">
            <Flex
                alignItems="center"
                rowGap="xsmall"
                columnGap="xsmall"
                wrap="wrap"
            >
                <div>
                    {Object.values(IHubContentType).map(
                        (ihubContentType, idx) => {
                            return (
                                <ToggleButton
                                    active={contentType === ihubContentType}
                                    type="button"
                                    data-testid={`toggle-content-${ihubContentType}`}
                                    onClick={() =>
                                        handleContentToggle(ihubContentType)
                                    }
                                    style={{
                                        marginLeft: 14 * (idx > 0 ? 1 : 0),
                                    }}
                                >
                                    {IHubContentTypeToLabelMap[ihubContentType]}
                                </ToggleButton>
                            );
                        },
                    )}
                </div>
                {enableSingleIdentifierView ? (
                    <StyledPill
                        data-testid="share-single-content-pill"
                        prefix={QueryParam.IDENTIFIER}
                        label={insightIdentifier}
                        onClose={handlePillClose}
                    />
                ) : (
                    <>
                        {<SearchBar />}
                        <TypeFilterButton typeFilterGroups={typeFilterGroups} />
                        {!!visibleFilters.length &&
                            visibleFilters.map((filter: DiscoveryFilter) => {
                                return (
                                    <FilterButton
                                        key={filter.field}
                                        {...{
                                            filter,
                                            fieldSelections:
                                                fieldSelections[filter.field],
                                            options:
                                                filterOptions[filter.field],
                                            onOptionClick: (option: Item) => {
                                                handleOptionClick(
                                                    filter.field,
                                                    option,
                                                );
                                            },
                                        }}
                                    />
                                );
                            })}
                        {!!additionalFilters.length && (
                            <AdditionalFilters
                                {...{
                                    options: additionalFilters,
                                    selectedValues: visibleFilters.map(
                                        (option) => option.field,
                                    ),
                                }}
                            />
                        )}
                        <TextButton
                            onClick={handleRemoveAllFilters}
                            disabled={!hasAppliedFilters()}
                            data-testid="remove-all-filters-button"
                        >
                            Clear added filters
                        </TextButton>
                        <TextButton
                            onClick={handleShareFilterClick}
                            data-testid="share-filters-button"
                        >
                            Share filters
                        </TextButton>
                    </>
                )}
            </Flex>
            <Text
                color="default"
                weight="bold"
                withMargin={true}
                hidden={!!advertiserId}
            >
                {ACCOUNT_AGNOSTIC_VIEW_WITH_ACCOUNT_SWITCHER}
            </Text>
        </FilterContainer>
    );
};
